import React, { useCallback, useEffect, useState } from 'react';
import { Row } from 'reactstrap';

import Pagination from 'components/Pagination';
import Loader from 'components/Loader';
import FilterOpportunitiesForm from './components/FilterOpportunitiesForm';
import ListOpportunitiesTable from './components/ListOpportunitiesTable';

import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import { useListOpportunities } from '../../../hooks/useListOpportunities';

import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import opportunityService from 'views/CRM/Opportunity/services/opportunityService';
import { toast } from 'react-toastify';

import { useListOpportunityStagesByRecordType } from 'views/CRM/OpportunityStages/hooks/useListOpportunityStagesByRecordType';
import { InfoCard } from './components/Kanban/components/Card';
import ChangeResponsibleModal from './components/ChangeResponsibleModal';
import { KanbanRefactor } from './components/KanbanRefactor';
import {
  useMemorizeFilters,
  POSSIBLE_FILTERS_ENTITIES
} from '../../../../../../hooks/useMemorizeInputsFilters';

const OpportunityCRM = ({
  selectedRecordTypeId,
  setSelectedRecordType,
  recordTypes
}) => {
  const [activePage, setActivePage] = useState(1);
  const [filters, setFilters] = useState({});
  const [showTable, setShowTable] = useState(true);
  const { opportunityStages, getOpportunityStagesByRecordType } =
    useListOpportunityStagesByRecordType();

  const [selectedRows, setSelectedRows] = useState([]);
  const [transferOpportunities, setTransferOpportunities] = useState(false);
  const [isChangeResponsibleModalOpen, setIsChangeResponsibleModalOpen] =
    useState(false);
  const [transferColumnsSearch, setTransferColumnsSearch] = useState({});
  const [stagePageInfo, setStagePageInfo] = useState([]);
  const [isLoadingKanban, setIsLoadingKanban] = useState(false);
  const [searchFiltersData, setSearchFiltersData] = useState({});
  const [kanbanData, setKanbanData] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null
  });

  useEffect(() => {
    getOpportunityStagesByRecordType(selectedRecordTypeId);
  }, [getOpportunityStagesByRecordType, selectedRecordTypeId]);

  const {
    getOpportunities,
    opportunities,
    totalOpportunities,
    totalAmount,
    isLoadingOpportunities,
    clearOpportunities,
    getAllOpportunities,
    getAndReturnOpportunities
  } = useListOpportunities({
    recordTypeId: selectedRecordTypeId,
    sortConfig
  });

  const { customFields, getCustomFields } = useEntityCustomFieldsByRecordType(
    entitiesName.OPPORTUNITY,
    selectedRecordTypeId
  );

  const { memorizeFilters, getMemorizedFilters } = useMemorizeFilters(
    POSSIBLE_FILTERS_ENTITIES.CRM_OPPORTUNITY
  );

  useEffect(() => {
    getCustomFields();
  }, [getCustomFields]);

  useEffect(() => {
    clearOpportunities(clearOpportunities);
  }, [selectedRecordTypeId, clearOpportunities]);

  useEffect(() => {
    handleSearch(getMemorizedFilters());
  }, [sortConfig]);

  const handleSearchKanban = (searchFilters) => {
    const stages = opportunityStages || [];
    const filteredStages = stages.filter((stage) => {
      return stage.name !== 'Sem Status';
    });

    const promises = filteredStages.map(async (stage) => {
      return getAndReturnOpportunities(1, {
        ...searchFilters,
        stageId: stage.id
      });
    });
    Promise.all(promises).then((responses) => {
      const dataObj = {};

      responses.forEach((response) => {
        const stageId = response.config.params.stageId;
        dataObj[stageId] = response.data.data;
      });

      const stageInfo = [];

      responses.forEach((response) => {
        const stage = opportunityStages.find(
          (stage) => stage.id === response.config.params.stageId
        );
        stageInfo.push({
          id: stage.id,
          name: stage.name,
          count: response.data.totalRegisters,
          currentPage: response.config.headers.page
        });
      });

      setStagePageInfo(stageInfo);

      setKanbanData(dataObj);
    });
    return;
  };

  const handleSearch = useCallback(
    (searchFilters, getAll = false) => {
      setSearchFiltersData(searchFilters);
      const page = searchFilters?.page || 1;

      setActivePage(page);
      setFilters(searchFilters);
      if (getAll) {
        handleSearchKanban(searchFilters);
        return;
      }
      getOpportunities(page, searchFilters);
    },
    [
      getOpportunities,
      getAllOpportunities,
      opportunityStages,
      handleSearchKanban,
      setSearchFiltersData
    ]
  );

  const handleTransferSearch = () => {
    handleSearch(transferColumnsSearch);
  };

  const handlePageChange = (newPage) => {
    const currentFilters = getMemorizedFilters();
    memorizeFilters({ ...currentFilters, page: newPage });
    setActivePage(newPage);
    getOpportunities(newPage, filters);
  };

  const reloadOpportunities = useCallback(() => {
    getOpportunities(1, {});
  }, [getOpportunities]);

  const onTableChange = async (oportunidadeId, faseId, _, oldStageId) => {
    await opportunityService.updateStage(oportunidadeId, { stageId: faseId });

    const oldStage = oldStageId;
    const newStage = faseId;
    const oldStageInfo = stagePageInfo.find((item) => item.id === oldStage);
    const newStageInfo = stagePageInfo.find((item) => item.id === newStage);

    const resultOld = await getAndReturnOpportunities(
      1,
      {
        ...searchFiltersData,
        stageId: oldStage
      },
      oldStageInfo.currentPage * 20
    );

    const resultNew = await getAndReturnOpportunities(
      1,
      {
        ...searchFiltersData,
        stageId: newStage
      },
      newStageInfo.currentPage * 20
    );

    if (!resultOld || !resultNew) {
      toast.error('Ocorreu um erro ao atualizar a fase');
      return false;
    }

    setKanbanData((prev) => {
      const copy = { ...prev };
      copy[oldStage] = resultOld.data.data;
      copy[newStage] = resultNew.data.data;
      return copy;
    });

    setStagePageInfo((prev) => {
      const copy = [...prev];
      copy.forEach((item) => {
        if (item.id === oldStage) {
          item.count = resultOld.data.totalRegisters;
        }
        if (item.id === newStage) {
          item.count = resultNew.data.totalRegisters;
        }
      });
      return copy;
    });
    return true;
  };

  const onTableGrow = async (stageId) => {
    if (isLoadingKanban) return;
    setIsLoadingKanban(true);
    try {
      const page = stagePageInfo.find(
        (item) => item.id === stageId
      ).currentPage;
      if (!page) {
        return;
      }
      const response = await getAndReturnOpportunities(page + 1, {
        ...searchFiltersData,
        stageId: stageId
      });

      setKanbanData((prev) => {
        const copy = { ...prev };
        copy[stageId] = [...copy[stageId], ...response.data.data];
        return copy;
      });
      setStagePageInfo((prev) => {
        const copy = [...prev];
        copy.forEach((item) => {
          if (item.id === stageId) {
            item.currentPage = page + 1;
          }
        });
        return copy;
      });
      setIsLoadingKanban(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleOnSelect = (row, isSelect) => {
    if (isSelect) {
      setSelectedRows([...selectedRows, row]);
    } else {
      setSelectedRows(selectedRows.filter((x) => x.id !== row.id));
    }
  };

  const handleOnSelectAll = (isSelect, rows) => {
    const allRows = rows.map((r) => {
      return r;
    });
    if (isSelect) {
      setSelectedRows(allRows);
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <>
      <FilterOpportunitiesForm
        handleSearch={handleSearch}
        customFields={customFields}
        isLoadingOpportunities={isLoadingOpportunities}
        recordTypeId={selectedRecordTypeId}
        setShowTable={setShowTable}
        showTable={showTable}
        opportunities={opportunities}
        totalOpportunities={totalOpportunities}
        selectedRecordTypeId={selectedRecordTypeId}
        setSelectedRecordType={setSelectedRecordType}
        recordTypes={recordTypes}
        setTransferOpportunities={setTransferOpportunities}
        transferOpportunities={transferOpportunities}
        selectedRows={selectedRows}
        setIsChangeResponsibleModalOpen={setIsChangeResponsibleModalOpen}
        setTransferColumnsSearch={setTransferColumnsSearch}
      />
      <Row
        style={{ overflowX: 'auto', width: '100%', height: '33.75rem' }}
        className="mt-2"
      >
        {isLoadingOpportunities ? (
          <div className="col-12">
            <Row className="justify-content-md-center">
              <Loader />
            </Row>
          </div>
        ) : showTable ? (
          <ListOpportunitiesTable
            opportunities={opportunities}
            reloadOpportunities={reloadOpportunities}
            customFields={customFields}
            transferOpportunities={transferOpportunities}
            handleOnSelect={handleOnSelect}
            handleOnSelectAll={handleOnSelectAll}
            selectedRows={selectedRows}
            totalAmount={totalAmount}
            setSortConfig={setSortConfig}
            sortConfig={sortConfig}
          />
        ) : (
          <KanbanRefactor
            reloadOpportunities={reloadOpportunities}
            onTableChange={onTableChange}
            data={Object.values(kanbanData).flat() ?? []}
            opportunityStages={opportunityStages ?? []}
            InfoCard={InfoCard}
            onTableGrow={onTableGrow}
            stagePageInfo={stagePageInfo}
          />
        )}
      </Row>
      {showTable && (
        <div style={{ marginTop: '3rem' }}>
          <Pagination
            activePage={activePage}
            totalItemsCount={totalOpportunities}
            onChange={handlePageChange.bind(this)}
          />
        </div>
      )}
      <ChangeResponsibleModal
        isModalOpen={isChangeResponsibleModalOpen}
        onModalToggle={setIsChangeResponsibleModalOpen}
        opportunityIds={selectedRows}
        callback={handleTransferSearch}
      />
    </>
  );
};

export default OpportunityCRM;